import { Component, computed, ElementRef, inject, OnInit, signal } from '@angular/core';
import { NgComponentOutlet, NgStyle } from '@angular/common';
import { cloneDeep, merge } from 'lodash-es';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { DropdownService } from './dropdown.service';
import { DropdownComponent } from './dropdown.component';
import { defaultDropdownOptions, DropdownContent, DropdownInputs, DropdownOptions } from './model';

@Component({
  selector: 'app-dropdown-layout',
  imports: [NgStyle, NgClickOutsideDirective, NgComponentOutlet],
  templateUrl: './dropdown-layout.component.html',
  styles: `
    :host { display: contents; }
    .dropdown-position { position: absolute; z-index: 9999; }
  `
})
export class DropdownLayoutComponent implements OnInit {

  dropdownClass = DropdownComponent
  dropdownContent = signal<DropdownContent|undefined>(undefined)
  positionHost = signal<HTMLElement|undefined>(undefined)
  options = signal<DropdownOptions|undefined>(undefined)

  protected inputs = computed(() => {
    const content = this.dropdownContent()
    const options = this.options()
    if (content && options) {
      const inputs: DropdownInputs = {content, options}
      return (inputs as any) as Record<string, unknown>
    }
    return undefined
  })

  protected style = computed(() => {
    const positionHost = this.positionHost()
    const options = this.options()
    if (positionHost && options) {
      return this.calcPosition(positionHost, options)
    }
    return undefined
  })

  private dropdown = inject(DropdownService)

  ngOnInit(): void {
    this.dropdown.registerLayout(this)
  }

  create(content: DropdownContent, positionHost: HTMLElement|ElementRef<HTMLElement>, options?: Partial<DropdownOptions>) {
    this.destroy()
    this.dropdownContent.set(content)
    this.positionHost.set(positionHost instanceof ElementRef ? positionHost.nativeElement : positionHost)
    this.options.set(merge(cloneDeep(defaultDropdownOptions), options))
  }

  destroy() {
    this.dropdownContent.set(undefined)
  }

  protected calcPosition(positionHost: HTMLElement, options: DropdownOptions): Record<string, string> {
    const bounds  = positionHost.getBoundingClientRect()
    const scrollY = window.scrollY || document.documentElement.scrollTop
    const scrollX = window.scrollX || document.documentElement.scrollLeft
    // Body padding
    const paddingX = 16;
    const paddingY = 10;

    const res: Record<string, string> = {
      'width': `${bounds.width}px`
    }

    switch (options.positionY) {
      case 'bottom':
        res['top'] = `${bounds.top + bounds.height + scrollY + options.offset - paddingY}px`
        break
      case 'top':
        res['bottom'] = `${bounds.top + scrollY - options.offset - paddingY}px`
        break
    }
    switch (options.positionX) {
      case 'left':
        res['left'] = `${bounds.left + scrollX - paddingX}px`
        break;
      case 'right':
        res['right'] = `${document.documentElement.scrollWidth - bounds.right - scrollX - paddingX}px`
        break;
    }

    return res;
  }

}
